import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #variantMediaRequestId = 0;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event without exposing a blank gallery while the
   * incoming variant image is still downloading. The old gallery remains visible
   * until the next primary image has loaded and decoded.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const requestId = ++this.#variantMediaRequestId;
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    await this.#waitForPrimaryImage(newMediaGallery);

    if (requestId !== this.#variantMediaRequestId || !this.isConnected) return;
    this.replaceWith(newMediaGallery);
  };

  /**
   * Loads and decodes the incoming gallery's first product image before swap.
   * In the normal path this is already in browser cache from variant-image-preload.js,
   * so the wait is effectively immediate.
   *
   * @param {Element} newMediaGallery
   */
  async #waitForPrimaryImage(newMediaGallery) {
    const incomingImage = newMediaGallery.querySelector(
      'img.product-media__image, img[srcset], img'
    );

    if (!(incomingImage instanceof HTMLImageElement)) return;

    const src = incomingImage.getAttribute('src');
    const srcset = incomingImage.getAttribute('srcset');
    const sizes = incomingImage.getAttribute('sizes');

    if (!src && !srcset) return;

    const preloader = new Image();
    preloader.decoding = 'async';
    preloader.fetchPriority = 'high';
    preloader.setAttribute('fetchpriority', 'high');

    await new Promise((resolve) => {
      let settled = false;
      let timeoutId;

      const finish = async (loaded) => {
        if (settled) return;
        settled = true;
        if (timeoutId) window.clearTimeout(timeoutId);

        if (loaded && typeof preloader.decode === 'function') {
          try {
            await preloader.decode();
          } catch (_error) {
            // A completed image can still reject decode in some browsers.
          }
        }

        resolve();
      };

      preloader.addEventListener('load', () => void finish(true), { once: true });
      preloader.addEventListener('error', () => void finish(false), { once: true });

      if (sizes) preloader.sizes = sizes;
      if (srcset) preloader.srcset = srcset;
      if (src) preloader.src = src;

      if (preloader.complete && preloader.naturalWidth > 0) {
        queueMicrotask(() => void finish(true));
      }

      // Safety valve for a pathological stalled request. In ordinary use this never fires.
      timeoutId = window.setTimeout(() => void finish(false), 8000);
    });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
